import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Star, ExternalLink, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import GoogleLogo from "@/components/site/GoogleLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Couleurs officielles Google
const G_BLUE = "#237cb5";
const G_RED = "#EA4335";
const G_YELLOW = "#FBBC05";
const G_GREEN = "#34A853";

const FALLBACK_RATING = 4.9;
const FALLBACK_COUNT = 588;

function FullStars({ value = 5, size = "h-5 w-5" }) {
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`Note ${value} sur 5`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={size}
          fill={G_YELLOW}
          stroke={G_YELLOW}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function AuthorAvatar({ author }) {
  const initial = (author?.name || "?").trim().charAt(0).toUpperCase();
  const [imgErr, setImgErr] = useState(false);
  if (author?.photoUri && !imgErr) {
    return (
      <img
        src={author.photoUri}
        alt={author.name}
        referrerPolicy="no-referrer"
        loading="lazy"
        onError={() => setImgErr(true)}
        className="h-11 w-11 rounded-full object-cover border border-[#e6edf3]"
      />
    );
  }
  // Fallback: initial inside a Google-blue circle
  return (
    <span
      className="h-11 w-11 rounded-full inline-flex items-center justify-center text-white font-bold text-base"
      style={{ background: G_BLUE }}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}

function ReviewCard({ review, onExpand, expanded }) {
  const text = review.text || "";
  const isLong = text.length > 160;
  const visible = expanded || !isLong ? text : text.slice(0, 150) + "…";

  return (
    <article
      data-testid="google-review-card"
      className="snap-start shrink-0 w-[85%] sm:w-[300px] lg:w-[320px] xl:w-[320px] bg-white rounded-2xl border border-[#e6edf3] p-5 hover:border-[#5793d8]/60 hover:shadow-[0_20px_40px_-24px_rgba(35,124,181,0.35)] transition-[transform,box-shadow,border-color,background-color,color] duration-300 flex flex-col"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <AuthorAvatar author={review.author} />
          <div className="min-w-0">
            <p className="font-semibold text-[#0f1d2b] truncate text-sm">{review.author?.name}</p>
            <p className="text-[11px] text-[#5b6b7a]">{review.relativeTime}</p>
          </div>
        </div>
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center h-7 w-7 rounded-full shrink-0"
          style={{ background: "#f7fafc" }}
        >
          <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
            <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
        </span>
      </div>

      <div className="mt-3">
        <FullStars value={review.rating || 5} size="h-3.5 w-3.5" />
      </div>

      <p className="mt-3 text-[13px] leading-relaxed text-[#3a4a5a] whitespace-pre-line flex-1">
        {visible}
      </p>

      {isLong && (
        <button
          type="button"
          onClick={onExpand}
          className="mt-2 self-start text-xs font-semibold text-[#237cb5] hover:text-[#143f5d]"
          data-testid="review-toggle-expand"
        >
          {expanded ? "Réduire" : "Lire la suite"}
        </button>
      )}
    </article>
  );
}

export default function Avis({ rotate = 0, eyebrow, titlePrefix, titleAccent, intro } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState({});
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/reviews`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rating = data?.rating ?? FALLBACK_RATING;
  const count = data?.userRatingCount ?? FALLBACK_COUNT;
  const rawReviews = data?.reviews || [];
  // Rotate the reviews order so different pages can show them in a different sequence
  const reviews = rotate && rawReviews.length
    ? [...rawReviews.slice(rotate % rawReviews.length), ...rawReviews.slice(0, rotate % rawReviews.length)]
    : rawReviews;
  const googleUri =
    data?.googleMapsUri ||
    "https://www.google.com/maps/place/BIG+D%C3%89M%C3%89NAGEMENT/@48.8800933,2.337217,17z/data=!4m6!3m5!1s0x47e6676f04cfb483:0x6d2b1e27c4d85b54!8m2!3d48.8800933!4d2.337217!16s%2Fg%2F11ry9cnkcv";

  // SEO: inject Review JSON-LD when reviews load.
  //
  // IMPORTANT: do NOT redeclare `aggregateRating` here — it's already
  // declared by the static `MovingCompany` in /public/index.html and
  // Google would otherwise flag "Review snippet contains multiple
  // cumulative ratings" in Search Console. We just attach the individual
  // `review` items to the same entity via `@id`.
  useEffect(() => {
    if (!reviews.length) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "MovingCompany",
      "@id": "https://bigdemenagement.fr/#movingcompany",
      review: reviews.slice(0, 5).map((r) => ({
        "@type": "Review",
        reviewRating: {
          "@type": "Rating",
          ratingValue: String(r.rating),
          bestRating: "5",
        },
        author: { "@type": "Person", name: r.author?.name || "Anonyme" },
        reviewBody: r.text || "",
      })),
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-google-reviews-jsonld", "true");
    tag.textContent = JSON.stringify(ld);
    document.head.appendChild(tag);
    return () => {
      if (tag.parentNode) tag.parentNode.removeChild(tag);
    };
  }, [reviews]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-testid='google-review-card']");
    const step = card ? card.getBoundingClientRect().width + 20 : 360;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      id="avis"
      data-testid="avis-section"
      className="relative py-10 sm:py-14 bg-white overflow-hidden"
    >
      {/* Subtle Google-coloured ambient corners */}
      <div
        className="absolute -top-40 left-0 right-0 h-80 blur-3xl opacity-20"
        style={{ background: G_BLUE }}
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-24 -right-32 h-72 w-72 rounded-full blur-3xl opacity-15"
        style={{ background: G_YELLOW }}
        aria-hidden="true"
      />

      <div className="bd-container relative">
        {/* Header — rating panel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#e6edf3] bg-white px-4 py-1.5 shadow-sm">
              <GoogleLogo className="h-4 w-auto" />
              <span className="text-xs font-semibold tracking-wide text-[#5b6b7a] uppercase">
                {eyebrow || "Avis vérifiés"}
              </span>
            </div>
            <h2 className="bd-h2 mt-5 text-[26px] sm:text-[30px] lg:text-[36px]">
              {titlePrefix || "Nos clients en parlent mieux que nous sur "}
              <span style={{ color: G_BLUE }}>{titleAccent || "Google"}</span>
            </h2>
            <p className="mt-5 text-[#3a4a5a] leading-relaxed max-w-xl">
              {intro || (
                <>
                  {count.toLocaleString("fr-FR")} clients ont laissé un avis vérifié sur Big Déménagement.
                  Ces témoignages sont récupérés en temps réel depuis notre fiche Google professionnelle.
                </>
              )}
            </p>
          </div>

          <div className="lg:col-span-5">
            <div
              data-testid="avis-rating-panel"
              className="rounded-3xl border-2 p-4 sm:p-5 bg-white relative max-w-[420px] lg:ml-auto"
              style={{ borderColor: "#e6edf3" }}
            >
              {/* Coloured top stripe (Google flag) */}
              <div className="absolute inset-x-0 top-0 h-1.5 flex rounded-t-3xl overflow-hidden" aria-hidden="true">
                <div className="flex-1" style={{ background: G_BLUE }} />
                <div className="flex-1" style={{ background: G_RED }} />
                <div className="flex-1" style={{ background: G_YELLOW }} />
                <div className="flex-1" style={{ background: G_GREEN }} />
              </div>

              <div className="flex items-center gap-3">
                <GoogleLogo className="h-6 w-auto" />
                <span className="text-sm font-semibold text-[#5b6b7a]">Note Big Déménagement</span>
              </div>
              <div className="mt-4 flex items-end justify-between gap-3">
                <div className="flex items-end gap-3">
                  <div data-testid="avis-rating-value" className="text-[42px] sm:text-5xl font-extrabold leading-none text-[#0f1d2b] tracking-tight">
                    {rating.toFixed(1).replace(".", ",")}
                  </div>
                  <div className="pb-2">
                    <FullStars size="h-5 w-5" />
                    <div className="text-xs text-[#5b6b7a] mt-1.5">
                      sur{" "}
                      <span
                        data-testid="avis-review-count"
                        className="font-extrabold text-xl"
                        style={{ color: G_BLUE }}
                      >
                        {count.toLocaleString("fr-FR")}
                      </span>{" "}
                      avis
                    </div>
                  </div>
                </div>
                <a
                  href={googleUri}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="avis-google-link"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap hover:underline underline-offset-4 pb-2"
                  style={{ color: G_BLUE }}
                >
                  Voir tous les avis
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews carousel */}
        <div className="mt-14">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base sm:text-lg font-bold text-[#0f1d2b] flex items-center gap-2">
              <Quote className="h-4 w-4" style={{ color: G_BLUE }} aria-hidden="true" />
              Derniers avis vérifiés
            </h3>
            <div className="hidden sm:flex items-center gap-2">
              <button
                type="button"
                aria-label="Précédent"
                data-testid="reviews-prev"
                onClick={() => scrollBy(-1)}
                className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-[#e6edf3] text-[#0f1d2b] hover:border-[#237cb5] hover:text-[#237cb5]"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Suivant"
                data-testid="reviews-next"
                onClick={() => scrollBy(1)}
                className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-[#e6edf3] text-[#0f1d2b] hover:border-[#237cb5] hover:text-[#237cb5]"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex gap-5 overflow-hidden">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="shrink-0 w-[420px] h-[260px] rounded-2xl border border-[#e6edf3] bg-[#f7fafc] animate-pulse"
                />
              ))}
            </div>
          ) : error || !reviews.length ? (
            <div className="rounded-2xl border border-[#e6edf3] bg-white p-6 text-center">
              <p className="text-[#3a4a5a]">
                Les avis ne peuvent pas être affichés pour le moment.{" "}
                <a href={googleUri} target="_blank" rel="noopener noreferrer" className="underline text-[#237cb5]">
                  Consultez-les directement sur Google
                </a>
                .
              </p>
            </div>
          ) : (
            <div
              ref={scrollerRef}
              data-testid="reviews-scroller"
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-1 px-1 scroll-smooth"
              style={{ scrollbarWidth: "thin" }}
            >
              {reviews.map((r) => (
                <ReviewCard
                  key={r.id}
                  review={r}
                  expanded={!!expanded[r.id]}
                  onExpand={() =>
                    setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
